import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import './FriendInvitePage.css';
import PageButton from '../../elements/PageButton/PageButton';
import Pagination from '../../elements/Pagination/Pagination';
import RewardListEditor from '../../elements/RewardListEditor/RewardListEditor';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {copyTextToClipboard} from '../../../utils/other/copyTextToClipboard';
import {getPartnerRewardDisplayProps} from '../../../utils/partner/getPartnerRewardDisplayProps';
import {
  buildFriendInviteShareMessage,
  getFriendInviteShareImageUrl
} from '../../../utils/partner/buildFriendInviteShareMessage';
import {getShareImageFile} from '../../../utils/share/getShareImageFile';
import {FRIEND_CRYSTALS_PURCHASE_BONUS_PERCENT, FRIEND_REFERAL_URL_PREFIX,} from '../../../constants/partner';
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";

const PAGE_ID = 'FriendInvitePage';
const PAGE_SIZE = 10;

function formatDate(isoString) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })}, ${date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}`;
}

// Компактный список наград в плашке зарегистрированного друга (метка +
// иконка); до клейма — «Награды еще не получены»
function RegistrationRewardsList({rewards, isClaimed}) {
  // span, а не div: список лежит внутри плашки-кнопки, а блочные элементы в
  // кнопку вкладывать нельзя (раскладку задает CSS)
  return (
    <span className="friend-invite-registration-rewards">
      {isClaimed && rewards.length > 0 ? (
        rewards.map((reward, index) => {
          const display = getPartnerRewardDisplayProps(reward);
          return (
            <span key={index} className={`friend-invite-registration-reward ${display.colorClass}`}>
              {display.fullLabel}
              {display.iconSrc && (
                <img
                  className="friend-invite-registration-reward-icon"
                  src={display.iconSrc}
                  alt=""
                />
              )}
            </span>
          );
        })
      ) : (
        <span className="friend-invite-registration-reward friend-invite-registration-reward_pending">
          Награды еще не получены
        </span>
      )}
    </span>
  );
}

// Список наград только для чтения — превью RewardListEditor без полей ввода
function RewardsPreviewList({rewards}) {
  if (!rewards || rewards.length === 0) {
    return <p className="friend-invite-status">Награды пока не настроены</p>;
  }
  return (
    <div className="friend-invite-rewards-list">
      <RewardListEditor
        rewards={rewards} onChange={() => {
      }} readOnly
      />
    </div>
  );
}

/**
 * Экран «Пригласить друга»: реферальная ссылка пользователя с «Поделиться» и
 * «Скопировать», награды программы (из глобального конфига админки) — другу
 * за регистрацию и пригласившему, плюс бонус с покупок кристаллов, и
 * пагинационный список зарегистрировавшихся по ссылке.
 *
 * Для админа (isAdminView) — тот же экран по targetUserId: видно, каких
 * друзей пригласил пользователь и как страница выглядит для него.
 */
function FriendInvitePage({
  targetUserId, targetUserName, targetReferalCode, isAdminView = false, onBack, onUserClick,
}) {
  usePageAnalytics('FriendInvitePage');

  const ownReferalCode = useSelector((state) => state.private.auth.friendReferalCode);

  // Ссылку адресует ПУБЛИЧНЫЙ реферальный код, а не id пользователя: она
  // расходится по мессенджерам, а id одновременно служит удостоверением для
  // входа в лобби и игрового сокета (см. ensureFriendReferalCode на сервере).
  // Откат на id — для сессий, открытых до появления кода, и для админского
  // просмотра пользователя, у которого кода еще нет: серверный разбор
  // принимает оба формата
  const referalLinkId = (isAdminView ? targetReferalCode : ownReferalCode) || targetUserId;
  const referalUrl = `${window.location.origin}${FRIEND_REFERAL_URL_PREFIX}${encodeURIComponent(referalLinkId)}`;

  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Сообщение пересобирается, когда подгрузились награды из конфига: до этого
  // оно уже осмысленно, но без перечня подарков (см. buildFriendInviteShareMessage)
  const shareMessage = useMemo(
    () => buildFriendInviteShareMessage(referalUrl, config?.friendRewards),
    [referalUrl, config?.friendRewards]
  );
  // Наглядная картинка подарка: сундук из наград, если он там есть
  const shareImageUrl = useMemo(
    () => getFriendInviteShareImageUrl(config?.friendRewards),
    [config?.friendRewards]
  );

  const [registrations, setRegistrations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [cache, setCache] = useState(new Map());

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/friend-referal/config`, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Не удалось загрузить награды');
        }
        setConfig(data);
      } catch (e) {
        console.error('Friend referal config error', e);
        setConfigError(e.message || 'Не удалось загрузить награды');
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    const fetchInvited = async () => {
      setListLoading(true);
      setListError(null);

      const cacheKey = `${isAdminView ? targetUserId : 'self'}_${page}`;
      const cached = cache.get(cacheKey);
      if (cached) {
        setRegistrations(cached.registrations);
        setTotalPages(cached.totalPages);
        setTotal(cached.total);
        setListLoading(false);
        return;
      }

      try {
        const url = isAdminView
          ? `${API_BASE}/admin/friend-referal/invited/${targetUserId}?limit=${PAGE_SIZE}&page=${page}`
          : `${API_BASE}/friend-referal/my-invited?limit=${PAGE_SIZE}&page=${page}`;
        const res = await fetch(url, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Не удалось загрузить список приглашенных');
        }
        setRegistrations(data.registrations || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
        setCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, data);
          return next;
        });
      } catch (e) {
        console.error('Invited friends error', e);
        setListError(e.message || 'Не удалось загрузить список приглашенных');
      } finally {
        setListLoading(false);
      }
    };

    fetchInvited();
  }, [page, cache, targetUserId, isAdminView]);

  const handleShare = async () => {
    // Нативная панель «Поделиться» (на мобильных — список приложений);
    // без поддержки share — фолбэк на копирование сообщения целиком
    if (navigator.share) {
      try {
        const shareData = {text: shareMessage};

        // Картинка — необязательное дополнение: файлы это Web Share второго
        // уровня, их поддерживают не все браузеры и не все цели отправки.
        // canShare проверяем именно с тем набором полей, который отправляем:
        // часть браузеров отказывается от files вместе с текстом
        const imageFile = await getShareImageFile(shareImageUrl);
        if (imageFile && navigator.canShare?.({...shareData, files: [imageFile]})) {
          shareData.files = [imageFile];
        }

        await navigator.share(shareData);
        return;
      } catch (e) {
        // Отмена пользователем — не ошибка
        if (e.name === 'AbortError') return;
        console.error('Share failed', e);
      }
    }
    try {
      await copyTextToClipboard(shareMessage);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 2000);
    } catch (e) {
      console.error('Share fallback copy failed', e);
      window.prompt('Скопируйте сообщение вручную:', shareMessage);
    }
  };

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(referalUrl);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy friend referal url', e);
      window.prompt('Не удалось скопировать автоматически. Скопируйте ссылку вручную:', referalUrl);
    }
  };

  const handlePageChange = (newPage) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setPage(newPage);
  };

  return (
    <>
      <h1 className="lobby-page-title friend-invite-page-title">
        Пригласить друга
        {isAdminView && targetUserName && (
          <span className="friend-invite-page-subtitle">{targetUserName}</span>
        )}
      </h1>

      <div className="friend-invite-content">
        <div className="friend-invite-link-block">
          <div className="friend-invite-link">{referalUrl}</div>
          <div className="friend-invite-link-actions">
            <button
              type="button"
              className="friend-invite-link-button"
              onClick={handleShare}
              title="Поделиться реферальной ссылкой"
            >
              Поделиться
            </button>
            <button
              type="button"
              className="friend-invite-link-button"
              onClick={handleCopy}
              title="Скопировать реферальную ссылку"
            >
              Скопировать
            </button>
            {isLinkCopied && <span className="friend-invite-link-copied">Скопировано!</span>}
          </div>
        </div>

        {configError && <p className="friend-invite-status friend-invite-status_error">{configError}</p>}

        <h2 className="friend-invite-section-title">Награды для друга за регистрацию:</h2>
        <RewardsPreviewList rewards={config?.friendRewards}/>

        <h2 className="friend-invite-section-title">Награды для тебя:</h2>
        <p className="friend-invite-inviter-label">За регистрацию:</p>
        <RewardsPreviewList rewards={config?.inviterRewards}/>

        <p className="friend-invite-crystals-note friend-invite-crystals-note-main">
          <img
            className={'friend-invite-crystals-img'}
            src={'/img/icons/crystal.png'}
            alt="Кристаллы"
            title={"Кристаллы"}
          />
          <span className="text-crystals">
            {config?.crystalsPurchaseBonusPercent ?? FRIEND_CRYSTALS_PURCHASE_BONUS_PERCENT}% кристаллов
          </span>
        </p>
        <p className="friend-invite-crystals-note">
          <span className="text-coins">
            Каждый раз, когда друг покупает кристаллы в Магазине, ты получаешь дополнительный бонус в таком размере.
          </span>
          <br/>Друг получает столько кристаллов, сколько приобрел.
          <br/>Тебе начисляются дополнительные кристаллы в подарок за каждую его покупку кристаллов.
        </p>

        <h2 className="friend-invite-section-title">Зарегистрировались по твоей ссылке:</h2>
        {listLoading && <p className="friend-invite-status">Загрузка...</p>}
        {listError && <p className="friend-invite-status friend-invite-status_error">{listError}</p>}
        {!listLoading && !listError && (
          registrations.length === 0 ? (
            <p className="friend-invite-status">Пока никто не зарегистрировался</p>
          ) : (
            <>
              <p className="friend-invite-total">
                Всего: <span className="text-coins">{total}</span>
              </p>
              <div className="friend-invite-registrations">
                {registrations.map((registration, index) => (
                  // Плашка кликабельна и ведет в профиль друга — как строка
                  // игрока в Зале славы (PageButton, чтобы работали клавиатура,
                  // звук и аналитика). Внутри только строчные элементы: блочные
                  // в кнопку вкладывать нельзя
                  <PageButton
                    key={registration.userId}
                    className="friend-invite-registration"
                    pageId={PAGE_ID}
                    buttonId={`registration_${index}`}
                    onClick={() => onUserClick?.(registration.userId, registration.userName)}
                  >
                    <span className="friend-invite-registration-header">
                      <span className="friend-invite-registration-name">{registration.userName}</span>
                      <span className="friend-invite-registration-date">{formatDate(registration.createdAt)}</span>
                    </span>
                    <span className={'friend-invite-registration-rewards-title'}>Друг получил награды:</span>
                    <RegistrationRewardsList
                      rewards={registration.rewards}
                      isClaimed={registration.hasReceivedPartnerReferalRewards}
                    />
                    {/* Награды пригласившего за этого друга (снапшот момента
                        клейма). Если клейм был, но наград пригласившему тогда
                        настроено не было — блок не показывается вовсе */}
                    {(!registration.hasReceivedPartnerReferalRewards
                      || (registration.inviterRewards || []).length > 0) && (
                      <>
                        <span className={'friend-invite-registration-rewards-title'}>Вы получили награды:</span>
                        <RegistrationRewardsList
                          rewards={registration.inviterRewards || []}
                          isClaimed={registration.hasReceivedPartnerReferalRewards}
                        />
                      </>
                    )}
                  </PageButton>
                ))}
              </div>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange}/>
            </>
          )
        )}
      </div>

      <div className="main-menu-buttons friend-invite-page-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default FriendInvitePage;
